import {
  IndexPrices,
  useIndexPrices,
} from "#/store/app/indexPrices/indexPrices";
import { useKyanWsErrors } from "#/store/app/kyanWsErrors/kyanWsErrors";
import { useLastTrades } from "#/store/app/lastTrades/lastTrades";
import { useComboPrices } from "#/store/combo/comboPrices";
import { useOptionchain } from "#/store/options/page/optionchain";
import { useOption } from "#/store/options/tradePanel/option";
import { useFundingRates } from "#/store/perps/page/fundingRates";
import { usePerpsOrderbook } from "#/store/perps/page/perpsOrderbook";
import { useInterestRateData } from "#/store/rfq/interestRateData";
import { useRfqRequests } from "#/store/rfq/rfqRequests";
import { useRfqResponses } from "#/store/rfq/rfqResponses";
import { useSviData } from "#/store/rfq/sviData";
import { useAccountState } from "#/store/wallet/account/accountState";
import { useAccountStateTimestamps } from "#/store/wallet/account/accountStateTimestamps";
import { useOpenOrders } from "#/store/wallet/account/openOrders";
import { usePositions } from "#/store/wallet/account/positions";
import {
  type AccountStateByPortfolioSymbol,
  type AccountStateTimestamps,
  type PositionsByPairs,
} from "#/types/accounts";
import { PricesTableEntry } from "#/types/combo";
import { FundingRates } from "#/types/funding";
import {
  InterestRateData,
  Option,
  OptionChainEntry,
  SviData,
} from "#/types/options";
import { TransformedOrderbook } from "#/types/orderbook";
import { OpenOrdersByPairs } from "#/types/orders";
import { LastTrades } from "#/types/perps";

import {
  Channel,
  KyanWsError,
  MessageContent,
  RfqContent,
  type WorkerToMainMessage,
} from "../../types/app/messages";

export function useProcessMessages() {
  const { setPerpsOrderbook } = usePerpsOrderbook();
  const { setFundingRates } = useFundingRates();
  const { setIndexPrices } = useIndexPrices();
  const { setKyanWsErrors } = useKyanWsErrors();
  const { setAccountState } = useAccountState();
  const { setAccountStateTimestamps } = useAccountStateTimestamps();
  const { setPositions } = usePositions();
  const { setOptionchain } = useOptionchain();
  const { setOption } = useOption();
  const { setOpenOrders } = useOpenOrders();
  const { setRfqRequests } = useRfqRequests();
  const { setRfqResponses } = useRfqResponses();
  const { setComboPrices } = useComboPrices();
  const { setLastTrades } = useLastTrades();
  const { setSviData } = useSviData();
  const { setInterestRateData } = useInterestRateData();

  const handlers: Partial<Record<Channel, (message: MessageContent) => void>> =
    {
      [Channel.Perps]: (message) =>
        setPerpsOrderbook(message as TransformedOrderbook),
      [Channel.Funding]: (message) => setFundingRates(message as FundingRates),
      [Channel.Prices]: (message) => {
        setIndexPrices(message as IndexPrices);
      },
      [Channel.AccountState]: (message) =>
        setAccountState(message as AccountStateByPortfolioSymbol),
      [Channel.AccountStateTimestamp]: (message) =>
        setAccountStateTimestamps(message as AccountStateTimestamps),
      [Channel.Positions]: (message) =>
        setPositions(message as PositionsByPairs),
      [Channel.OpenOrders]: (message) =>
        setOpenOrders(message as OpenOrdersByPairs),
      [Channel.RFQ]: (message) => {
        const content = message as RfqContent;
        setRfqRequests(content.rfqRequests);
        setRfqResponses(content.rfqResponses);
      },
      [Channel.Optionchain]: (message) =>
        setOptionchain(message as OptionChainEntry[]),
      [Channel.Option]: (message) => {
        const newOption = message as Option;

        setOption((prev) => {
          if (!prev || prev.instrument_name !== newOption.instrument_name)
            return prev;

          return {
            ...prev,
            bids: newOption.bids,
            asks: newOption.asks,
            markPrice: newOption.markPrice,
            delta: newOption.delta, // needed to apply correct slippage to marker orders;
          };
        });
      },
      [Channel.ComboPrices]: (message) =>
        setComboPrices(message as PricesTableEntry[]),
      [Channel.IV]: (message) => {
        setSviData(message as SviData);
      },
      [Channel.InterestRate]: (message) => {
        setInterestRateData(message as InterestRateData);
      },
      [Channel.Trade]: (message) => setLastTrades(message as LastTrades),
      [Channel.Error]: (message) => {
        const content = message as KyanWsError;
        setKyanWsErrors((previous) => ({
          ...previous,
          [content.data.subscription.channel]: content,
        }));
      },
    };

  function processMessages(message: WorkerToMainMessage) {
    handlers[message.channel]?.(message.content as MessageContent);
  }

  return { processMessages };
}
